import React from 'react';

import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

import { formatCurrencyBRL, temperatureLabel } from './detailsFormat';
import type { DetailsData, DetailsLayerId, DetailsMediaCounts } from './useDetailsData';

interface DetailsIndexProps {
  data: DetailsData;
  onOpen: (layer: DetailsLayerId) => void;
}

/**
 * O travessão de `detailsFormat`. Aqui ele tem dois papéis: é o valor de
 * comparação para saber se o formatador achou dado ou não — o resumo do índice
 * é UMA linha e não pode virar "— · —" — e é o próprio resumo da linha cujo
 * dado a raiz não carrega.
 */
const TRACO = '—';

/** Ordem e plural do resumo de arquivos, verbatim do desenho aprovado. */
const TIPOS_DE_MIDIA: ReadonlyArray<{
  key: Exclude<keyof DetailsMediaCounts, 'total'>;
  um: string;
  muitos: string;
}> = [
  { key: 'image', um: 'imagem', muitos: 'imagens' },
  { key: 'video', um: 'vídeo', muitos: 'vídeos' },
  { key: 'document', um: 'documento', muitos: 'documentos' },
  { key: 'audio', um: 'áudio', muitos: 'áudios' },
];

/** "4 imagens · 1 vídeo · 2 documentos" — tipo zerado não entra na conta. */
function resumoDeArquivos(counts: DetailsMediaCounts): string {
  const partes = TIPOS_DE_MIDIA.filter((t) => counts[t.key] > 0).map((t) => {
    const n = counts[t.key];
    return `${n} ${n === 1 ? t.um : t.muitos}`;
  });
  return partes.length > 0 ? partes.join(' · ') : 'nenhum arquivo';
}

/**
 * Etapa, valor e temperatura, nessa ordem, separados por ponto médio — a ordem
 * do desenho aprovado. A etapa vem hidratada na própria busca de leads por contato
 * (o backend faz Two-Phase Fetch de `step` e `funnel` nessa rota), então ela
 * está disponível já na raiz — não é preciso esperar a camada de Negócio.
 */
function resumoDoNegocio(lead: DetailsData['lead']): string {
  if (!lead) return 'sem negócio no funil';

  const partes: string[] = [];
  const etapa = lead.step?.name.trim();
  if (etapa) partes.push(etapa);
  const valor = formatCurrencyBRL(lead.wonValue ?? lead.budget);
  if (valor !== TRACO) partes.push(valor);
  const temperatura = temperatureLabel(lead.temperature);
  if (temperatura !== TRACO) partes.push(temperatura);

  // Há negócio, mas sem etapa, valor nem temperatura: dizer isso é melhor que
  // uma linha vazia, que se leria como defeito de layout.
  return partes.length > 0 ? partes.join(' · ') : 'negócio no funil';
}

function resumoDeMembros(total: number): string {
  if (total === 0) return TRACO;
  return `${total} ${total === 1 ? 'pessoa' : 'pessoas'}`;
}

interface RowProps {
  id: DetailsLayerId;
  icon: IconName;
  title: string;
  summary: string;
  /** Contagem à direita. Só aparece quando há o que contar. */
  badge?: number | undefined;
  onOpen: (layer: DetailsLayerId) => void;
}

const Row: React.FC<RowProps> = ({ id, icon, title, summary, badge, onOpen }) => (
  <button
    type="button"
    className="cdp-row"
    data-testid={`details-row-${id}`}
    onClick={() => onOpen(id)}
  >
    <span className="cdp-rowic">
      <Icon name={icon} className="w-4 h-4" />
    </span>
    <span className="cdp-rowtx">
      <span className="cdp-rowt">{title}</span>
      <span className="cdp-rows">{summary}</span>
    </span>
    {typeof badge === 'number' && badge > 0 && <span className="cdp-rowbadge">{badge}</span>}
    <span className="cdp-rowcta">
      <Icon name="chevron-right" className="w-4 h-4" />
    </span>
  </button>
);

/**
 * O índice — a raiz do painel.
 *
 * Cada linha é um assunto, com um resumo de UMA linha que serve para decidir se
 * vale entrar. Nada aqui busca dado: o resumo sai do que `useDetailsData` já
 * carrega na raiz (contagem de mídia e lead); o resto carrega ao abrir a camada.
 */
const DetailsIndex: React.FC<DetailsIndexProps> = ({ data, onOpen }) => {
  const telefone = data.conversation?.contact?.phone?.trim();
  const arquivos = resumoDeArquivos(data.mediaCounts);
  const totalDeMembros = data.conversation?.members?.length ?? 0;

  return (
    <div className="cdp-index" data-testid="details-index">
      <div className="cdp-grouplabel">{data.isGroup ? 'Grupo' : 'Cadastro'}</div>

      {data.isGroup ? (
        <Row
          id="membros"
          icon="users"
          title="Membros"
          summary={resumoDeMembros(totalDeMembros)}
          badge={totalDeMembros}
          onOpen={onOpen}
        />
      ) : (
        <>
          <Row
            id="contato"
            icon="user"
            title="Contato"
            summary={telefone && telefone.length > 0 ? telefone : 'sem telefone'}
            onOpen={onOpen}
          />
          {/* Sem empresa vinculada não há linha: ela levaria a uma camada vazia. */}
          {data.companyName && (
            <Row
              id="empresa"
              icon="briefcase"
              title="Empresa"
              summary={data.companyName}
              onOpen={onOpen}
            />
          )}
          <Row
            id="negocio"
            icon="target"
            title="Negócio"
            summary={resumoDoNegocio(data.lead)}
            onOpen={onOpen}
          />
        </>
      )}

      <div className="cdp-grouplabel">Conversa</div>

      <Row
        id="arquivos"
        icon="folder"
        title="Arquivos"
        summary={arquivos}
        badge={data.mediaCounts.total}
        onOpen={onOpen}
      />
      {/*
        A agenda só é buscada quando a camada de Retornos abre — na raiz o painel
        não sabe se há retorno marcado. Por isso o resumo é um travessão: dizer
        "nenhum agendado" seria afirmar como certo algo que não foi verificado, e
        esconderia justamente o retorno que a pessoa precisa ver.
      */}
      <Row id="retornos" icon="calendar" title="Retornos" summary={TRACO} onOpen={onOpen} />
    </div>
  );
};

export default DetailsIndex;
